// Judgment scoring engine.
// Pure functions for computing calibration tiers and narrative insights from
// a JudgmentProfile. No I/O — operates entirely on already-fetched data.

import type {
  JudgmentProfile,
  JudgmentSummary,
  JudgmentTier,
  JudgmentDomain,
} from '../types/decisionIntelligence';

// Brier-score-based tier classification.
// calibrationScore = 1 - mean(brier_error), range [0,1].
// Perfect forecaster = 1.0. Random = ~0.75. Worse than random < 0.75.
function calibrationTier(score: number | null, resolvedCount: number): JudgmentTier {
  if (resolvedCount < 3) return 'insufficient_data';
  if (score === null)    return 'insufficient_data';
  if (score >= 0.85)     return 'superforecaster';
  if (score >= 0.70)     return 'strong';
  return 'developing';
}

const DOMAIN_LABELS: Record<JudgmentDomain, string> = {
  product:      'product decisions',
  strategy:     'strategic bets',
  operations:   'operational calls',
  hiring:       'hiring decisions',
  architecture: 'architecture choices',
  gtm:          'go-to-market moves',
  resource:     'resource allocation',
  technical:    'technical judgments',
  general:      'decisions',
};

export function buildJudgmentSummary(profile: JudgmentProfile): JudgmentSummary {
  const tier = calibrationTier(profile.calibrationScore, profile.resolvedCount);
  const domain = DOMAIN_LABELS[profile.domain] ?? 'decisions';
  const strengths: string[] = [];
  const cautions: string[] = [];

  if (tier === 'insufficient_data') {
    return { profile, tier, strengths: [], cautions: ['Needs more resolved decisions to score.'] };
  }

  // Calibration narrative
  if (profile.calibrationScore !== null) {
    const pct = Math.round(profile.calibrationScore * 100);
    if (pct >= 85) strengths.push(`Exceptionally well-calibrated on ${domain} (${pct}% calibration).`);
    else if (pct >= 70) strengths.push(`Well-calibrated on ${domain} (${pct}% calibration).`);
    else cautions.push(`Calibration on ${domain} needs improvement (${pct}%).`);
  }

  // Confidence appropriateness
  if (profile.confidenceAppropriateness !== null) {
    const ca = profile.confidenceAppropriateness;
    if (ca >= 0.70) strengths.push('Confidence levels align well with actual outcomes.');
    else if (ca < 0.40) cautions.push('Confidence levels are poorly matched to outcomes — may be over- or under-confident.');
  }

  // Proceed accuracy
  if (profile.proceedAccuracy !== null) {
    const pa = Math.round(profile.proceedAccuracy * 100);
    if (pa >= 75) strengths.push(`Strong go/no-go accuracy: ${pa}% of "proceed" calls succeeded.`);
    else if (pa < 50) cautions.push(`"Proceed" calls succeed only ${pa}% of the time — green-lights may need scrutiny.`);
  }

  // Sample size context
  const resolved = profile.resolvedCount;
  if (resolved >= 20) strengths.push(`High-confidence profile: ${resolved} resolved decisions.`);
  else if (resolved >= 5) strengths.push(`Growing track record: ${resolved} resolved decisions.`);

  return { profile, tier, strengths, cautions };
}

// Given a set of profiles (e.g. for a whole workspace), return the top N
// by calibration for a specific domain. Returns general profiles as fallback.
export function topVoicesForDomain(
  profiles: JudgmentProfile[],
  domain: JudgmentDomain,
  topN = 5
): JudgmentProfile[] {
  const inDomain = profiles
    .filter(p => p.domain === domain && p.resolvedCount >= 3)
    .sort((a, b) => (b.calibrationScore ?? 0) - (a.calibrationScore ?? 0));

  if (inDomain.length >= topN) return inDomain.slice(0, topN);

  // Pad with general profiles not already in the list
  const alreadyIn = new Set(inDomain.map(p => p.userId));
  const general = profiles
    .filter(p => p.domain === 'general' && !alreadyIn.has(p.userId) && p.resolvedCount >= 3)
    .sort((a, b) => (b.calibrationScore ?? 0) - (a.calibrationScore ?? 0));

  return [...inDomain, ...general].slice(0, topN);
}

// Short tier label + color hint for UI chips
export const TIER_META: Record<JudgmentTier, { label: string; color: string }> = {
  superforecaster:  { label: 'Superforecaster', color: 'text-[var(--green)] bg-[var(--green-dim)] dark:text-[var(--text-ter)] dark:bg-[var(--green)]/40' },
  strong:           { label: 'Strong',          color: 'text-[var(--text-sec)]    bg-[var(--slate-dim)]    dark:bg-[var(--slate)]/40'    },
  developing:       { label: 'Developing',      color: 'text-[var(--mustard)]   bg-[var(--mustard-dim)]   dark:text-[var(--mustard)]   dark:bg-[var(--mustard)]/40'   },
  insufficient_data:{ label: 'New',             color: 'text-[var(--text-ter)]    bg-[var(--hover-tint)]    dark:bg-[var(--bg-raised)]/60'    },
};
